import React, { Component } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import { humanize, titleize } from 'underscore.string';
import classnames from 'classnames';
import DatePicker from 'react-datepicker';
import DataList from 'components/ui/DataList';
import DataPoller from 'components/ui/DataPoller';
import Badge from 'components/ui/Badge';
import CustomerSearch from 'components/site/customer-search-alt';
import OrdersActionCreators from 'actions/OrdersActionCreators';
import OrdersStore from 'stores/OrdersStore';
import OrdersCsvStore from 'stores/OrdersCsvStore';

import Container from 'components/v2/container';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import FilterGroup from './FilterGroup';
import CreateOrderModal from './CreateOrderModal';
import Searchbox from './SearchBox';
import Mapping from './ListMapping';
import Filters from './Filters';
import EquipmentSearch from './equipments/EquipmentSearch';
import { sluggify } from '../../../utils/localization/utils';

const HIDDEN_COLUMNS_KEY = 'ORDERS_HIDDEN_COLS';
const LIMIT = 1000;

class List extends Component {
  constructor(props) {
    super(props);
    const hiddenColumnsString = localStorage.getItem(HIDDEN_COLUMNS_KEY);
    let hiddenColumns;
    try {
      hiddenColumns = JSON.parse(hiddenColumnsString) || [];
    } catch (e) {
      hiddenColumns = [];
    }

    this.state = {
      selected: [],
      orders: [],
      pages: 1,
      hiddenColumns,
      searchTerm: '',
      filtersToggled: false,
      filters: {
        outstanding_balance: [],
        status: [],
        reference: [],
        shipper: [],
        carrier: [],
        equipment: [],
        last_free_date: null,
        estimated_arrival: null,
        arrival_date: null,
        page: 1,
        per_page: 25,
        paginate: true,
        sort_by: '_id',
        order: 'desc', // Questionable here but server side expects order currently.
        steamship_or_railroad: '',
        vessel_or_rail: '',
        arriving: '',
        delivering: '',
        insufficient_documentation: [],
        email_sent: [],
      },
      metaData: {
        users: {},
        equipment: {},
        shipperTextfield: '',
        carrierTextfield: '',
        steamship_or_railroad_input: '',
        vessel_or_rail_input: '',
        arriving_input: '',
        delivering_input: '',
      },
      showCreateOrder: false,
      downloadCSVDisabled: false,
    };
  }

  componentDidMount() {
    OrdersStore.addChangeListener(this.handleOrdersStoreChange);
    OrdersCsvStore.addChangeListener(this.handleOrdersCsvStoreChange);
    let pathUrl = this.props.match.url;
    if (pathUrl.includes('/order-booking')) {
      this.setState({ showCreateOrder: true });
    }
  }

  componentWillUnmount() {
    OrdersStore.removeChangeListener(this.handleOrdersStoreChange);
    OrdersCsvStore.removeChangeListener(this.handleOrdersCsvStoreChange);
  }

  getColumnList() {
    const { t } = this.props;
    const hiddenColumns = this.state.hiddenColumns;
    return Mapping.columns
      .filter((c) => c.hideable)
      .map((column) => ({
        dataField: column.dataField,
        text: titleize(t(column.localizationSlug)),
        hidden: hiddenColumns.includes(column.dataField),
      }));
  }

  getUnhidableColumns() {
    return Mapping.columns
      .filter((x) => x.hideable === false)
      .map((c) => c.dataField);
  }

  getFilterParams() {
    const { filters } = this.state;

    const mappedFilters = cloneDeep(filters);

    if (mappedFilters.arrival_date) {
      mappedFilters.arrival_date = mappedFilters.arrival_date.toJSON();
    }
    if (mappedFilters.estimated_arrival) {
      mappedFilters.estimated_arrival =
        mappedFilters.estimated_arrival.toJSON();
    }
    if (mappedFilters.last_free_date) {
      mappedFilters.last_free_date = mappedFilters.last_free_date.toJSON();
    }

    if (!mappedFilters.order_type) {
      delete mappedFilters.order_type;
    }

    if (!mappedFilters.service_type) {
      delete mappedFilters.service_type;
    }

    if (!mappedFilters.vessel_or_rail) {
      delete mappedFilters.vessel_or_rail;
    }

    if (!mappedFilters.steamship_or_railroad) {
      delete mappedFilters.steamship_or_railroad;
    }
    if (!mappedFilters.arriving) {
      delete mappedFilters.arriving;
    }
    if (!mappedFilters.delivering) {
      delete mappedFilters.delivering;
    }

    return mappedFilters;
  }

  getOrders() {
    OrdersActionCreators.getOrders(this.getFilterParams());
  }

  handleExportToCSV = () => {
    this.setState(
      {
        downloadCSVDisabled: true,
      },
      () => {
        const filters = this.getFilterParams();
        const activeColumns = this.getColumnList().map((c) => c.dataField);
        const hiddenColumns = this.getUnhidableColumns();
        const csvColumns = hiddenColumns.concat(activeColumns);

        OrdersActionCreators.downloadOrdersCsv(filters, LIMIT, csvColumns);
      },
    );
  };

  handleOrdersCsvStoreChange = () => {
    const isDownloaded = OrdersCsvStore.isFileDownloaded();
    this.setState({ downloadCSVDisabled: !isDownloaded });
  };

  handleTextFilterChange = (id) => (e) => {
    const inputField = `${id}_input`;
    const inputFieldValue = e?.target?.value;
    this.setState((state) => {
      const metaData = cloneDeep(state.metaData);
      metaData[inputField] = inputFieldValue;
      return {
        metaData,
      };
    });
  };

  handleApplyTextFilter = (id) => () => {
    const inputField = `${id}_input`;
    this.setState(
      (state) => {
        const value = state.metaData[inputField];
        const metaData = cloneDeep(state.metaData);
        const filters = cloneDeep(state.filters);
        metaData[inputField] = '';
        filters[id] = value;
        return {
          metaData,
          filters,
        };
      },
      () => this.getOrders(),
    );
  };

  handleToggleFilters = () => {
    this.setState((state) => ({
      filtersToggled: !state.filtersToggled,
    }));
  };

  handleCloseCreateOrder = () => this.setState({ showCreateOrder: false });

  handleOpenCreateOrder = () => this.setState({ showCreateOrder: true });

  handleShipperFieldChange = (value) => {
    this.setState((state) => {
      const metaData = cloneDeep(state.metaData);
      metaData.shipperTextfield = value;
      return { metaData };
    });
  };

  handleShipperSelect = (_item, value) => {
    this.setState(
      (state) => {
        const filters = cloneDeep(state.filters);
        const metaData = cloneDeep(state.metaData);
        const id = value._id;
        filters.shipper = [...filters.shipper, id];

        metaData.users[id] = value.company_name;
        metaData.shipperTextfield = '';

        return {
          metaData,
          filters,
        };
      },
      () => this.getOrders(),
    );
  };

  handleCarrierFieldChange = (value) => {
    this.setState((state) => {
      const metaData = cloneDeep(state.metaData);
      metaData.carrierTextfield = value;
      return {
        metaData,
      };
    });
  };

  handleCarrierSelect = (item, value) => {
    this.setState(
      (state) => {
        const filters = cloneDeep(state.filters);
        const metaData = cloneDeep(state.metaData);
        const id = value._id;
        filters.carrier = [...filters.carrier, id];

        metaData.users[id] = value.name;
        metaData.carrierTextfield = '';

        return {
          metaData,
          filters,
        };
      },
      () => this.getOrders(),
    );
  };

  handleEquipmentSelect = (value) => {
    this.setState(
      (state) => {
        const filters = cloneDeep(state.filters);
        const metaData = cloneDeep(state.metaData);
        const id = value._id;
        filters.equipment = [...filters.equipment, id];

        metaData.equipment[id] = value.equipment_id;

        return {
          metaData,
          filters,
        };
      },
      () => this.getOrders(),
    );
  };

  handleDropdownSelect = (groupId) => (item) => {
    console.log(item);
    this.setState(
      (state) => {
        const filters = cloneDeep(state.filters);
        filters[groupId] = item.value;
        return { filters };
      },
      () => this.getOrders(),
    );
  };

  handleFilterChange = (groupId) => (value) => (checked) => {
    this.setState(
      (state) => {
        const { filters: currentFilters } = state;

        const filters = { ...currentFilters };
        const index = filters[groupId].findIndex((f) => f === value);

        if (index !== -1 && !checked) {
          filters[groupId] = [
            ...filters[groupId].slice(0, index),
            ...filters[groupId].slice(index + 1),
          ];

          return {
            filters,
          };
        }
        if (index === -1 && checked) {
          filters[groupId].push(value);
          return { filters };
        }
      },
      () => this.getOrders(),
    );
  };

  handleDateChange = (groupId) => (date) => {
    this.setState(
      (state) => {
        const { filters: currentFilters } = state;

        const filters = cloneDeep(currentFilters);
        filters[groupId] = date;
        return {
          filters,
        };
      },
      () => this.getOrders(),
    );
  };

  handleDateSelect = (groupId) => (date) => {
    this.setState(
      (state) => {
        const { filters: currentFilters } = state;

        const filters = { ...currentFilters };
        filters[groupId] = date;
        return { filters };
      },
      () => this.getOrders(),
    );
  };

  handleFetchOrders = () => {
    this.getOrders();
  };

  handleOrdersStoreChange = () => {
    const payload = OrdersStore.getOrdersPayload();
    const { orders, pages, total: totalEntries } = payload;

    this.setState({
      orders,
      pages,
      totalEntries,
    });
  };

  handlePaginationSelect = (page) => {
    this.setState(
      (state) => {
        const filters = cloneDeep(state.filters);
        filters.page = page;
        return { filters };
      },
      () => this.getOrders(),
    );
  };

  handleSelectRow = (row, selected) => {
    const { selected: selectedRows } = this.state;

    if (!selected) {
      const index = selectedRows.indexOf(row._id);
      this.setState({
        selected: [...selected.slice(0, index), ...selected.slice(index + 1)],
      });
    } else {
      this.setState({
        selected: [...selected, row._id],
      });
    }
  };

  handleSelectAll = (selected, rows) => {
    if (!selected) {
      this.setState({
        selected: [],
      });
    } else {
      this.setState({ selected: rows.map((r) => r._id) });
    }
  };

  handleColumnToggle = (dataField) => (checked) => {
    const index = this.state.hiddenColumns.indexOf(dataField);
    const { hiddenColumns: currentHiddenColumns } = this.state;

    let hiddenColumns;
    if (checked && index !== -1) {
      hiddenColumns = [
        ...currentHiddenColumns.slice(0, index),
        ...currentHiddenColumns.slice(index + 1),
      ];
    } else if (!checked && index === -1) {
      hiddenColumns = [...currentHiddenColumns, dataField];
    } else {
      hiddenColumns = [...currentHiddenColumns];
    }

    this.setState({
      hiddenColumns,
    });
    localStorage.setItem(HIDDEN_COLUMNS_KEY, JSON.stringify(hiddenColumns));
  };

  handleSearchTermChange = (searchTerm) => {
    // search orders
    this.setState({
      searchTerm,
    });
  };

  handleSearch = () => {
    this.setState(
      (state) => {
        const { filters: currentFilters, searchTerm } = state;

        const filters = cloneDeep(currentFilters);
        if (searchTerm) {
          filters.reference = [searchTerm];
        } else {
          delete filters.reference;
        }
        return { filters };
      },
      () => this.getOrders(),
    );
  };

  handleRemoveShipper = (id) => () => {
    this.setState(
      (state) => {
        const filters = cloneDeep(state.filters);
        filters.shipper = filters.shipper.filter((c) => c !== id);
        const metaData = cloneDeep(state.metaData);
        delete metaData.users[id];

        return { metaData, filters };
      },
      () => this.getOrders(),
    );
  };

  handleRemoveCarrier = (id) => () => {
    this.setState(
      (state) => {
        const filters = cloneDeep(state.filters);
        filters.carrier = filters.carrier.filter((c) => c !== id);
        const metaData = cloneDeep(state.metaData);
        delete metaData.users[id];

        return { filters, metaData };
      },
      () => this.getOrders(),
    );
  };

  handleRemoveTextFilter = (id) => () => {
    this.setState(
      (state) => {
        const filters = cloneDeep(state.filters);
        filters[id] = '';
        return { filters };
      },
      () => this.getOrders(),
    );
  };

  handleRemoveEquipment = (id) => () => {
    this.setState(
      (state) => {
        const filters = cloneDeep(state.filters);
        filters.equipment = filters.equipment.filter((c) => c !== id);
        const metaData = cloneDeep(state.metaData);
        delete metaData.users[id];

        return {
          filters,
          metaData,
        };
      },
      () => this.getOrders(),
    );
  };

  isCSVDisabled() {
    return this.state.downloadCSVDisabled;
  }

  renderSelectedShippers() {
    return this.state.filters.shipper.map((c) => {
      const label = this.state.metaData.users[c];
      if (!label) {
        return <Badge text={c} key={c} onClick={this.handleRemoveShipper(c)} />;
      }
      return (
        <Badge text={label} key={c} onClick={this.handleRemoveShipper(c)} />
      );
    });
  }

  renderSelectedCarriers() {
    return this.state.filters.carrier.map((c) => {
      const label = this.state.metaData.users[c];
      if (!label) {
        return <Badge text={c} key={c} onClick={this.handleRemoveCarrier(c)} />;
      }
      return (
        <Badge text={label} key={c} onClick={this.handleRemoveCarrier(c)} />
      );
    });
  }

  renderSelectedTextFilter(id) {
    const value = this.state.filters[id];

    if (!value) {
      return null;
    }
    return <Badge text={value} onClick={this.handleRemoveTextFilter(id)} />;
  }

  renderSelectedEquipment() {
    return this.state.filters.equipment.map((c) => {
      const label = this.state.metaData.equipment[c];
      if (!label) {
        return (
          <Badge text={c} key={c} onClick={this.handleRemoveEquipment(c)} />
        );
      }
      return (
        <Badge text={label} key={c} onClick={this.handleRemoveEquipment(c)} />
      );
    });
  }

  renderColumnList() {
    return (
      <Popover id="order-columns" bsStyle="popover max-w-sm">
        <div className="w-full p-2">
          {this.getColumnList().map((col) => {
            const checked = !col.hidden;
            return (
              <CargomaticInput
                type="checkbox"
                id={col.dataField}
                className="my-2"
                applyDefaultMargins={false}
                key={col.dataField}
                checked={checked}
                onChange={this.handleColumnToggle(col.dataField)}
                label={col.text}
              />
            );
          })}
        </div>
      </Popover>
    );
  }

  renderFilterSidebar() {
    if (!this.state.filtersToggled) {
      return null;
    }
    return (
      <div className="w-full mt-4 md:w-1/4 bg-grey-lightest">
        {this.renderFilterGroups()}
      </div>
    );
  }

  renderCarrierFilter() {
    const { t } = this.props;
    return (
      <fieldset>
        <div className="mb-4 customer-search">
          <CustomerSearch
            clearOnSelect
            onChange={this.handleCarrierFieldChange}
            onSelect={this.handleCarrierSelect}
            value={this.state.metaData.carrierTextfield}
            placeholder={t('SEARCH_FOR_CARRIER')}
            type="carriers"
          />

          <div className="my-4 selected">{this.renderSelectedCarriers()}</div>
        </div>
      </fieldset>
    );
  }

  renderShipperFilter() {
    const { t } = this.props;
    return (
      <fieldset>
        <div className="mb-4 customer-search">
          <CustomerSearch
            clearOnSelect
            onChange={this.handleShipperFieldChange}
            onSelect={this.handleShipperSelect}
            value={this.state.metaData.shipperTextfield}
            placeholder={t('SEARCH_FOR_SHIPPER')}
            type="shippers"
          />

          <div className="my-4 selected">{this.renderSelectedShippers()}</div>
        </div>
      </fieldset>
    );
  }

  renderEquipmentFilter() {
    return (
      <fieldset>
        <div className="mb-4 customer-search">
          <EquipmentSearch onEquipmentSelect={this.handleEquipmentSelect} />

          <div className="my-4 selected">{this.renderSelectedEquipment()}</div>
        </div>
      </fieldset>
    );
  }

  renderTextFilter(filterGroup) {
    const { id } = filterGroup;
    const { t } = this.props;
    const value = this.state.filters[id];
    const field = this.state.metaData[`${id}_input`];
    const placeholer = t('FILTER_BY_VALUE', {
      value: t(filterGroup?.localizationSlug),
    });
    return (
      <fieldset>
        {!value && (
          <div className="flex mb-4 text-filter">
            <input
              type="text"
              className="form-field"
              placeholder={placeholer}
              value={field}
              onChange={this.handleTextFilterChange(id)}
            />
            <button
              className="button button-green"
              aria-label="Apply Filter"
              onClick={this.handleApplyTextFilter(id)}
            >
              <i className="mr-2 fa fa-check" />
            </button>
          </div>
        )}
        <div className="my-4 selected">{this.renderSelectedTextFilter(id)}</div>
      </fieldset>
    );
  }

  renderDropdownFilter(filterGroup) {
    const id = filterGroup.id;
    const { t } = this.props;
    const items = filterGroup.filters.map((f) => {
      if (typeof f === 'object') {
        const { value, name, localizationSlug } = f;
        return {
          value,
          name: titleize(t(localizationSlug)),
        };
      }
      return {
        value: f,
        name: humanize(f),
      };
    });

    return (
      <CargomaticSelect
        showCaption
        key={id}
        id={id}
        selected={this.state.filters[id]}
        valueKey="value"
        textKey="name"
        items={items}
        caption={t('SELECT')}
        hideLabel
        onSelect={this.handleDropdownSelect(id)}
      />
    );
  }

  renderFilter(groupId, filter) {
    const filters = this.state.filters[groupId] || [];
    const { t } = this.props;
    return (
      <CargomaticInput
        id={filter}
        key={filter}
        type="checkbox"
        className="my-2"
        checked={filters.includes(filter)}
        onChange={this.handleFilterChange(groupId)(filter)}
        applyDefaultMargins={false}
        label={titleize(t(sluggify(filter)))}
      />
    );
  }

  renderDateFilter(filterGroup) {
    const { t } = this.props;
    const selected = this.state.filters[filterGroup.id];
    return (
      <div className="block mt-4 mb-4 date-filter">
        <DatePicker
          selected={selected}
          className="w-full form-field"
          placeholderText={titleize(t(filterGroup.localizationSlug))}
          onChange={this.handleDateChange(filterGroup.id)}
          isClearable
          onSelect={this.handleDateSelect(filterGroup.id)}
        />
      </div>
    );
  }

  renderFilterGroup(filterGroup) {
    if (filterGroup.type === 'date') {
      return this.renderDateFilter(filterGroup);
    }

    if (filterGroup.type === 'equipment') {
      return this.renderEquipmentFilter(filterGroup);
    }

    if (filterGroup.type === 'shippers') {
      return this.renderShipperFilter();
    }
    if (filterGroup.type === 'carriers') {
      return this.renderCarrierFilter();
    }

    if (filterGroup.type === 'dropdown') {
      return this.renderDropdownFilter(filterGroup);
    }

    if (filterGroup.type === 'text') {
      return this.renderTextFilter(filterGroup);
    }
    const renderedFilters = filterGroup.filters.map((filter) =>
      this.renderFilter(filterGroup.id, filter),
    );
    return renderedFilters;
  }

  renderFilterGroups() {
    const { t } = this.props;
    return Filters.map((filterGroup) => {
      const title = filterGroup.localizationSlug;
      const groupId = filterGroup.id;
      const renderedFilterGroup = this.renderFilterGroup(filterGroup);
      return (
        <FilterGroup title={titleize(t(title))} key={groupId}>
          {renderedFilterGroup}
        </FilterGroup>
      );
    });
  }

  /*
   * This method is called on table change for all table events. We currently only
   * support remote sorting for the columns on this page.
   */
  onTableChange = (type, newState) => {
    this.setState(
      (state) => {
        const filters = { ...state.filters };

        if (type === 'sort') {
          const sortField = newState.sortField;
          switch (sortField) {
            case 'arriving[0]':
              filters.sort_by = `arriving.address`;
              break;
            case 'delivering[0]':
              filters.sort_by = `delivering.address`;
              break;
            default:
              filters.sort_by = sortField;
          }

          filters.order = newState.sortOrder;
          return {
            filters,
          };
        }
      },
      () => this.getOrders(),
    );
  };

  render() {
    const filterButtonClass = classnames({
      button: true,
      'button-circle': true,
      'button-green': this.state.filtersToggled,
      'button-hollow-green': !this.state.filtersToggled,
      'mr-2': true,
      flex: true,
      block: true,
      'items-center': true,
    });
    const mainClassName = classnames({
      'w-full': true,
      'md:w-3/4': this.state.filtersToggled,
    });
    const { t } = this.props;
    return (
      <Container className="py-8">
        <div className="flex items-center justify-between px-4 filters">
          <div className="w-full first-column md:w-1/2">
            <div className="flex items-center">
              <button
                className={filterButtonClass}
                title={t('TOGGLE_FILTERS')}
                onClick={this.handleToggleFilters}
                aria-label="Toggle Filter"
              >
                <span className="fa fa-filter" />
              </button>
              <Searchbox
                searchTerm={this.state.searchTerm}
                onChange={this.handleSearchTermChange}
                onSearch={this.handleSearch}
              />
            </div>
          </div>
          <div className="flex items-center second-column">
            <div className="mr-2">
              <button
                id="create-order-button"
                title={t('CREATE_ORDER')}
                onClick={this.handleOpenCreateOrder}
                className="button button-green order-createOrderModal-createOrder heap"
              >
                {t('CREATE_ORDER')}
              </button>
            </div>
            <div className="mr-2">
              <button
                title={t('EXPORT_TO_CSV')}
                className="flex item-center button button-hollow-green"
                disabled={this.isCSVDisabled()}
                onClick={this.handleExportToCSV}
              >
                <i className="mr-2 fa fa-file-excel-o" />
                {titleize(t('EXPORT'))}
              </button>
            </div>
            <OverlayTrigger
              overlay={this.renderColumnList()}
              placement="bottom"
              trigger="click"
            >
              <button className="flex items-center button button-hollow-green">
                <i className="mr-2 fa fa-columns" />
                <span>{titleize(t('COLUMN', { count: 1 }))}</span>
              </button>
            </OverlayTrigger>
          </div>
        </div>
        <DataPoller action={this.handleFetchOrders} />
        <div className="flex flex-wrap">
          {this.renderFilterSidebar()}
          <div className={mainClassName}>
            <div className="px-4 py-4">
              <DataList
                remote={{ sort: true }}
                onTableChange={this.onTableChange}
                showHeaders
                paginate
                striped
                data={this.state.orders}
                columns={Mapping.columns}
                hiddenColumns={this.state.hiddenColumns}
                total={this.state.totalEntries}
                perPage={this.state.filters.per_page}
                pages={this.state.pages}
                page={this.state.filters.page}
                onPaginationSelect={(page) => this.handlePaginationSelect(page)}
                selected={this.state.selected}
                onSelect={(row, selected) => {
                  this.handleSelectRow(row, selected);
                }}
                onSelectAll={(selected, rows) => {
                  this.handleSelectAll(selected, rows);
                }}
              />
            </div>
          </div>
        </div>
        {this.state.showCreateOrder && (
          <CreateOrderModal
            show={this.state.showCreateOrder}
            onClose={this.handleCloseCreateOrder}
          />
        )}
      </Container>
    );
  }
}

export default withTranslation()(List);
